import { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const formatCurrency = (value) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2,
}).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const today = () => new Date().toISOString().slice(0, 10);

export default function GstPage() {
  const [summary, setSummary] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeposit, setShowDeposit] = useState(false);
  const [savingDeposit, setSavingDeposit] = useState(false);
  const [depositForm, setDepositForm] = useState({ amount: '', deposit_date: today(), reference_no: '', notes: '' });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, depositsRes] = await Promise.all([
        adminApi.getGstSummary({}),
        adminApi.getGstDeposits(),
      ]);
      setSummary(summaryRes.data);
      setDeposits(depositsRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load GST dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const saveDeposit = async () => {
    const amount = Number(depositForm.amount || 0);
    if (amount <= 0) {
      toast.error('Enter a valid deposit amount');
      return;
    }
    setSavingDeposit(true);
    try {
      await adminApi.createGstDeposit({ ...depositForm, amount });
      toast.success('GST deposit recorded');
      setShowDeposit(false);
      setDepositForm({ amount: '', deposit_date: today(), reference_no: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record GST deposit');
    } finally {
      setSavingDeposit(false);
    }
  };

  const totals = summary?.summary || {};
  const items = summary?.items || [];

  return (
    <div className="p-8 space-y-6 bg-[#F8F6F3] min-h-screen">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">GST Dashboard</h1>
          <p className="text-[#A8A8A8] mt-1">Invoice tax snapshots, GST collection, and deposit tracking.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
          <Button onClick={() => setShowDeposit(true)}><Plus className="h-4 w-4 mr-2" />Record Deposit</Button>
        </div>
      </div>

      {loading ? (
        <Card><CardContent className="p-10 text-center text-[#A8A8A8]">Loading GST dashboard...</CardContent></Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card><CardContent className="p-5"><p className="text-sm text-[#A8A8A8]">Taxable Sales</p><p className="text-2xl font-bold text-[#2D2420]">{formatCurrency(totals.taxable_amount)}</p></CardContent></Card>
            <Card><CardContent className="p-5"><p className="text-sm text-[#A8A8A8]">CGST + SGST</p><p className="text-2xl font-bold text-[#2D2420]">{formatCurrency((totals.cgst_amount || 0) + (totals.sgst_amount || 0))}</p></CardContent></Card>
            <Card><CardContent className="p-5"><p className="text-sm text-[#A8A8A8]">IGST</p><p className="text-2xl font-bold text-[#2D2420]">{formatCurrency(totals.igst_amount)}</p></CardContent></Card>
            <Card><CardContent className="p-5"><p className="text-sm text-[#A8A8A8]">Pending Deposit</p><p className="text-2xl font-bold text-[#C97A5E]">{formatCurrency(totals.pending_deposit_amount)}</p></CardContent></Card>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <Card className="xl:col-span-2">
              <CardHeader>
                <CardTitle className="text-[#2D2420]">GST Invoice Register</CardTitle>
                <CardDescription>Orders with generated GST invoice snapshots.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Invoice</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Customer</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Taxable</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">CGST</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">SGST</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">IGST</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5]">
                      {items.length === 0 ? (
                        <tr><td colSpan="6" className="py-8 text-center text-[#A8A8A8]">No GST invoices generated yet</td></tr>
                      ) : items.map((item) => (
                        <tr key={item.id}>
                          <td className="py-3 px-4"><p className="font-medium text-[#2D2420]">{item.gst_invoice_no}</p><p className="text-xs text-[#A8A8A8]">{item.order_id}</p></td>
                          <td className="py-3 px-4 text-[#5C4033]">{item.customer_name}</td>
                          <td className="py-3 px-4 text-right">{formatCurrency(item.taxable_amount)}</td>
                          <td className="py-3 px-4 text-right">{formatCurrency(item.cgst_amount)}</td>
                          <td className="py-3 px-4 text-right">{formatCurrency(item.sgst_amount)}</td>
                          <td className="py-3 px-4 text-right">{formatCurrency(item.igst_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-[#2D2420]">GST Deposits</CardTitle>
                <CardDescription>Manual records of deposited GST.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {deposits.length === 0 ? <p className="text-sm text-[#A8A8A8]">No deposits recorded.</p> : deposits.map((deposit) => (
                  <div key={deposit.id} className="rounded-lg border border-[#E5E5E5] p-3">
                    <div className="flex justify-between gap-3">
                      <p className="font-semibold text-[#2D2420]">{formatCurrency(deposit.amount)}</p>
                      <p className="text-xs text-[#A8A8A8]">{formatDate(deposit.deposit_date)}</p>
                    </div>
                    <p className="text-sm text-[#5C4033]">{deposit.reference_no || 'No reference'}</p>
                    {deposit.notes ? <p className="text-xs text-[#A8A8A8] mt-1">{deposit.notes}</p> : null}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </>
      )}

      <Dialog open={showDeposit} onOpenChange={setShowDeposit}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-[#2D2420]">Record GST Deposit</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Amount</Label><Input type="number" value={depositForm.amount} onChange={(e) => setDepositForm((prev) => ({ ...prev, amount: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Deposit Date</Label><Input type="date" value={depositForm.deposit_date} onChange={(e) => setDepositForm((prev) => ({ ...prev, deposit_date: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Reference No</Label><Input value={depositForm.reference_no} onChange={(e) => setDepositForm((prev) => ({ ...prev, reference_no: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Notes</Label><Textarea value={depositForm.notes} onChange={(e) => setDepositForm((prev) => ({ ...prev, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeposit(false)}>Cancel</Button>
            <Button onClick={saveDeposit} disabled={savingDeposit}>{savingDeposit ? 'Saving...' : 'Save Deposit'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
